import React, { useEffect, useState } from "react";
import styles from "./index.module.scss";
import { ReactComponent as CloseSvg } from "../../assets/CloseSvg.svg";
import { ReactComponent as Plus } from "../../assets/PlusCircle.svg";
import {
  Button,
  FormControl,
  MenuItem,
  Modal,
  Select,
  Container,
  Box,
  OutlinedInput,
  Typography,
  FormHelperText,
  IconButton,
  Divider,
} from "@mui/material";

import { FormattedMessage, useIntl } from "react-intl";
import { useFormik } from "formik";
import { loader } from "graphql.macro";
import { useMutation } from "@apollo/client";
import Error from "../Error";
import { ReactComponent as CheckSvg } from "../../assets/modalCheckSvg.svg";

const ADD_USER_TO_ORGANIZATION = loader(
  "../../schema/mutation/AddUserToOrganization.graphql"
);
const CREATE_BILLING_PORTAL_SESSION = loader(
  "../../schema/mutation/CreateBillingPortalSession.graphql"
);

interface Props {
  organizationId: string;
  seatsQuotaReachedModal?: boolean;
  freeToBusinessModal?: boolean;
  // Only the owner can subscribe / manage billing; admins get a hint instead.
  isOwner?: boolean;
}

interface Data {
  addUserToOrganization: Organization;
}

interface PortalData {
  createBillingPortalSession: { url: string };
}

interface FormValues {
  email: string;
  role: string;
  membershipType: string;
}

const AddMemberModal: React.FC<Props> = ({
  organizationId,
  seatsQuotaReachedModal = false,
  freeToBusinessModal = false,
  isOwner = false,
}) => {
  const intl = useIntl();
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const initialValues: FormValues = {
    email: "",
    role: "MEMBER",
    membershipType: "INTERNAL",
  };

  const [addUserToOrganization, { error, reset }] = useMutation<
    Data,
    FormValues
  >(ADD_USER_TO_ORGANIZATION, {
    onCompleted: handleClose,
  });

  const [createBillingPortalSession] = useMutation<PortalData>(
    CREATE_BILLING_PORTAL_SESSION
  );
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  // Closes the modal and scrolls to the plans section, where the owner can
  // subscribe to the Business plan to get more licensed users.
  const handleGoToPlans = () => {
    handleClose();
    setTimeout(() => {
      document
        .getElementById("billing-plans")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  // Opens the Stripe billing portal, where the owner can click "Modify
  // subscription" to change the number of licensed users.
  const handleManageBilling = async () => {
    setPortalError(null);
    setPortalLoading(true);
    try {
      const { data } = await createBillingPortalSession({
        variables: { organizationId },
      });
      const url = data?.createBillingPortalSession.url;
      if (url) {
        window.location.href = url;
      } else {
        setPortalLoading(false);
      }
    } catch (e) {
      // Note: the imported `Error` component shadows the global Error here.
      const message = (e as { message?: string })?.message;
      setPortalError(message || "Something went wrong.");
      setPortalLoading(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validate: (values: FormValues) => {
      const errors: Partial<FormValues> = {};

      if (!values.email) {
        errors.email = intl.formatMessage({
          id: "errors.required",
          defaultMessage: "Required",
        });
      } else if (
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
      ) {
        errors.email = intl.formatMessage({
          id: "errors.invalid-email-address",
          defaultMessage: "Invalid email address",
        });
      }

      return errors;
    },
    onSubmit: (values, { setSubmitting }) => {
      const data = {
        ...values,
        fullname: "",
        organizationId: organizationId,
      };
      addUserToOrganization({
        variables: data,
      }).catch(() => {
        setSubmitting(false);
      });
    },
  });

  useEffect(() => {
    if (!show) {
      formik.resetForm();
      reset();
    }
    // eslint-disable-next-line
  }, [show]);

  return (
    <>
      <Button onClick={handleShow} startIcon={<Plus />} variant="contained">
        <FormattedMessage
          id="manage-organization.add-a-user"
          defaultMessage="Add a user"
        />
      </Button>

      <Modal open={show} onClose={handleClose}>
        <>
          {freeToBusinessModal && (
            <Box
              className={styles.dialog}
              sx={{
                bgcolor: "background.paper",
              }}
            >
              <CheckSvg />
              <Typography className={styles.title}>
                <FormattedMessage
                  id="action-moving-to-paid-plan"
                  defaultMessage="Move to a paid plan"
                />
              </Typography>

              <Typography className={styles.secondaryText}>
                {isOwner ? (
                  <FormattedMessage
                    id="action-moving-to-paid-plan-text"
                    defaultMessage="The free plan allows up to 4 members. To add more, subscribe to Starter or Pro Security: click 'Let's go' to go to the plans below and subscribe."
                  />
                ) : (
                  <FormattedMessage
                    id="ask-owner-to-subscribe-paid-plan"
                    defaultMessage="The free plan allows up to 4 members. To add more, ask your organization owner to subscribe to Starter or Pro Security."
                  />
                )}
              </Typography>
              <Box className={styles.actions} component="form">
                <Button
                  onClick={handleClose}
                  className={styles.cancelButton}
                  variant="contained"
                  color="secondary"
                >
                  <FormattedMessage
                    id="deleteModalBackButton"
                    defaultMessage="Back to Organization"
                  />
                </Button>
                {isOwner && (
                  <Button
                    className={styles.validateButton}
                    type="button"
                    variant="contained"
                    onClick={handleGoToPlans}
                  >
                    <FormattedMessage
                      id="actions.letsgo"
                      defaultMessage="Let's go"
                    />
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {seatsQuotaReachedModal && (
            <Box
              className={styles.dialog}
              sx={{
                bgcolor: "background.paper",
              }}
            >
              <CheckSvg />
              <Typography className={styles.title}>
                <FormattedMessage
                  id="seatsQuotaReachedTitle"
                  defaultMessage="Maximum number of members reached"
                />
              </Typography>

              <Typography className={styles.secondaryText}>
                {isOwner ? (
                  <FormattedMessage
                    id="seatsQuotaReachedText"
                    defaultMessage='You reached the maximum number of licensed users in your organization with your current plan. Click "Yes" to open the billing page, then click "Modify subscription" to increase the number of licensed users.'
                  />
                ) : (
                  <FormattedMessage
                    id="askOwnerToManageSeats"
                    defaultMessage="You reached the maximum number of licensed users in your organization with your current plan. Ask your organization owner to increase the number of licensed users."
                  />
                )}
              </Typography>
              <Box className={styles.actions} component="form">
                <Button
                  onClick={handleClose}
                  className={styles.cancelButton}
                  variant="contained"
                  color="secondary"
                >
                  <FormattedMessage
                    id="deleteModalBackButton"
                    defaultMessage="Back to Organization"
                  />
                </Button>
                {isOwner && (
                  <Button
                    className={styles.validateButton}
                    type="button"
                    variant="contained"
                    onClick={handleManageBilling}
                    disabled={portalLoading}
                  >
                    <FormattedMessage
                      id="action-delete.yes"
                      defaultMessage="Yes"
                    />
                  </Button>
                )}
              </Box>
              {portalError && (
                <Typography
                  className={styles.secondaryText}
                  sx={{ color: "error.main", mt: 1 }}
                >
                  {portalError}
                </Typography>
              )}
            </Box>
          )}

          {!freeToBusinessModal && !seatsQuotaReachedModal ? (
            <Container
              disableGutters
              maxWidth={false}
              className={styles.container}
              sx={{
                bgcolor: "background.paper",
              }}
            >
              <Typography className={styles.title}>
                <FormattedMessage
                  id="manage-organization.add-a-user"
                  defaultMessage="Add a user"
                />
                <IconButton onClick={handleClose}>
                  <CloseSvg />
                </IconButton>
              </Typography>

              <Box
                component="form"
                id="createMember"
                onSubmit={formik.handleSubmit}
              >
                <Box sx={{ p: 3 }}>
                  <Error error={error} />
                  <Box className={styles.inlineField}>
                    <Typography className={styles.text}>
                      <FormattedMessage
                        id="manage-organization.add-email-address"
                        defaultMessage="Add your invitee email address"
                      />
                    </Typography>
                    <FormControl fullWidth sx={{ maxWidth: "250px" }}>
                      <OutlinedInput
                        placeholder="Email"
                        name="email"
                        type="email"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                        required
                      />
                      <FormHelperText
                        sx={{ position: "absolute", top: "35px" }}
                      >
                        {formik.touched.email && formik.errors.email}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                  <Box className={styles.inlineField}>
                    <Typography className={styles.text}>
                      <FormattedMessage
                        id="manage-organization.select-role"
                        defaultMessage="Select user role"
                      />
                    </Typography>
                    <FormControl fullWidth sx={{ maxWidth: "250px" }}>
                      <Select
                        name="role"
                        id="role"
                        value={formik.values.role}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        aria-describedby="roleHelpBlock"
                      >
                        <MenuItem value="MEMBER">
                          {intl.formatMessage({
                            id: "Member",
                            defaultMessage: "Member",
                          })}
                        </MenuItem>
                        <MenuItem value="ADMIN">
                          {intl.formatMessage({
                            id: "Admin",
                            defaultMessage: "Admin",
                          })}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Typography className={styles.explanation}>
                      <FormattedMessage
                        id="manage-organization.select-role-message"
                        defaultMessage="Admin role can add new members."
                      />
                    </Typography>
                  </Box>
                  <Box className={styles.inlineField} sx={{ mb: 0 }}>
                    <Typography className={styles.text}>
                      <FormattedMessage
                        id="membership-type"
                        defaultMessage="Membership type"
                      />
                    </Typography>
                    <FormControl fullWidth sx={{ maxWidth: "250px" }}>
                      <Select
                        name="membershipType"
                        id="membershipType"
                        value={formik.values.membershipType}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        // aria-describedby="membershipTypeHelpBlock"
                      >
                        <MenuItem value="INTERNAL">
                          {intl.formatMessage({
                            id: "Internal",
                            defaultMessage: "Internal",
                          })}
                        </MenuItem>
                        <MenuItem value="EXTERNAL">
                          {intl.formatMessage({
                            id: "External",
                            defaultMessage: "External",
                          })}
                        </MenuItem>
                      </Select>

                      {/* <Typography
                        id="membershipTypeHelpBlock"
                        sx={{ maxWidth: "250px" }}
                        className={styles.text}
                      >
                        <FormattedMessage
                          id="manage-organization.select-membership-type-message"
                          defaultMessage="Some rooms may only be visible to internal users. External users won't see those rooms."
                        />
                      </Typography> */}
                    </FormControl>
                  </Box>
                </Box>
              </Box>
              <Divider />
              <Box className={styles.modalFooter}>
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  color="secondary"
                >
                  <FormattedMessage
                    id="actions.cancel"
                    defaultMessage="Cancel"
                  />
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  form="createMember"
                  disabled={formik.isSubmitting}
                >
                  <FormattedMessage id="actions.add" defaultMessage="Add" />
                </Button>
              </Box>
            </Container>
          ) : null}
        </>
      </Modal>
    </>
  );
};

export default AddMemberModal;
